import { CommandTrackingView } from "@/types/command-tracking.type";
import { useEffect, useState, Fragment } from "react";
import { AreaStateAggregateType } from "../../types/area-state-aggregate.type";
import { MODULE_RESPONSE_TIMEOUT_DELAY_SECONDS } from "../../hooks/use-command-time-progression";
import tw from "twin.macro";
import { AreaStatusDonut } from "./chart-donut";
import {
  BatteryFullIcon,
  BatteryLowIcon,
  BatteryMediumIcon,
  Check,
  ChevronRight,
  CloudSun,
  History,
  HourglassIcon,
  RefreshCcw,
  Shield,
  TriangleAlertIcon,
  WrenchIcon,
} from "lucide-react";
import { BottomSheet, useBottomSheetStack } from "@/components/BottomSheet";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { ModuleCommandEnum } from "@/types/enums/module-command.enum";
import { ModulesStatesEnum } from "../../types/enums/modules-states.enum";
import { useAreaDataProvider } from "../area-data-loader";
import { useSendCommand } from "@/api/areas/mutations";
import { TFunction } from "i18next";
import { toast } from "sonner";
import { SelectableRow } from "@/components/SelectableRow";
import { SheetsType } from "@/sheets/types/sheets.type";
import { ModulesFirstOfList } from "@/sheets/types/first-of-list.type";
import { switchSheetsTitles } from "@/sheets/switchs/switch-sheets-titles";
import { SheetsEnum } from "@/sheets/enums/sheets.enum";
import { switchSheetsLeadIcons } from "@/sheets/switchs/switch-sheets-lead-icons";
import { switchSheetsHeaderComponents } from "@/sheets/switchs/switch-sheets-header-components";
import { SwitchSheetsRender } from "@/sheets/switchs/switch-sheets-render";
import Up from "../up";
import Folded from "../folded";
import Unfolded from "../unfolded";

// Ré-export pour compatibilité : la constante vit désormais avec le hook
// `useIsOutOfTimeChrono`.
export { MODULE_RESPONSE_TIMEOUT_DELAY_SECONDS };

const toggleCommand = (
  command: ModuleCommandEnum.FOLD | ModuleCommandEnum.UNFOLD
) => {
  return command === ModuleCommandEnum.FOLD
    ? ModuleCommandEnum.UNFOLD
    : ModuleCommandEnum.FOLD;
};

const doesMatchResumeSequence = (areaCommandTracking: CommandTrackingView) => {
  return (
    areaCommandTracking.command === ModuleCommandEnum.RESUME &&
    areaCommandTracking.passedActivities[0]?.command ===
      ModuleCommandEnum.PAUSE &&
    (areaCommandTracking.passedActivities[1]?.command ===
      ModuleCommandEnum.FOLD ||
      areaCommandTracking.passedActivities[1]?.command ===
        ModuleCommandEnum.UNFOLD)
  );
};

const doesMatchCancelSequence = (areaCommandTracking: CommandTrackingView) => {
  return (
    areaCommandTracking.command === ModuleCommandEnum.CANCEL &&
    areaCommandTracking.passedActivities[0]?.command ===
      ModuleCommandEnum.PAUSE &&
    (areaCommandTracking.passedActivities[1]?.command ===
      ModuleCommandEnum.FOLD ||
      areaCommandTracking.passedActivities[1]?.command ===
        ModuleCommandEnum.UNFOLD)
  );
};

const getLastCommandView = (areaCommandTracking: CommandTrackingView) => {
  if (doesMatchResumeSequence(areaCommandTracking)) {
    return areaCommandTracking.passedActivities[1]!.command as
      | ModuleCommandEnum.FOLD
      | ModuleCommandEnum.UNFOLD;
  } else if (doesMatchCancelSequence(areaCommandTracking)) {
    return toggleCommand(
      areaCommandTracking.passedActivities[1]!.command as
        | ModuleCommandEnum.FOLD
        | ModuleCommandEnum.UNFOLD
    );
  } else {
    return areaCommandTracking.command;
  }
};

const getBadgeButton = ({
  openModulesList,
  areaStateAggregate,
  translator,
}: {
  areaStateAggregate: AreaStateAggregateType;
  openModulesList: (firstOfList: ModulesFirstOfList) => void;
  translator: TFunction<"translation", undefined>;
}) => {
  return (
    <button
      type="button"
      css={[
        tw`text-sm font-normal px-2 py-1 rounded-md flex items-center gap-1 h-fit`,
        tw`bg-primary-800/30 text-primary-800`,
        areaStateAggregate.numberOfFaultyModules > 0 &&
          tw`bg-modules-redBase/30 text-modules-redBase`,
        areaStateAggregate.numberOfWaitingForStateModules > 0 &&
          tw`bg-modules-amberBase/30 text-modules-amberBase`,
      ]}
      onClick={() => {
        if (areaStateAggregate.numberOfFaultyModules > 0) {
          openModulesList(ModulesStatesEnum.FAULTY);
        } else if (areaStateAggregate.numberOfWaitingForStateModules > 0) {
          openModulesList(ModulesStatesEnum.IN_PROGRESS);
        } else {
          openModulesList(
            areaStateAggregate.numberOfFoldedModules >
              areaStateAggregate.numberOfUnfoldedModules
              ? ModulesStatesEnum.FOLDED
              : ModulesStatesEnum.UNFOLDED
          );
        }
      }}
    >
      {areaStateAggregate.numberOfFaultyModules > 0 ? (
        <Fragment>
          <TriangleAlertIcon css={[tw`w-4 h-4`]} />
          <span tw="pb-0.5">{translator("area.someModulesAreFaulty")} </span>
          <span>→</span>
        </Fragment>
      ) : areaStateAggregate.numberOfWaitingForStateModules > 0 ? (
        <Fragment>
          <HourglassIcon css={[tw`w-4 h-4`]} />
          <span tw="pb-0.5">{translator("area.actionStillInProgress")}</span>
        </Fragment>
      ) : (
        <Fragment>
          <Check css={[tw`w-4 h-4`]} />
          <span tw="pb-0.5">{translator("area.allGood")}</span>
        </Fragment>
      )}
    </button>
  );
};

const getStateOfChargeIcon = (stateOfCharge: number) => {
  if (stateOfCharge && stateOfCharge < 30) {
    return <BatteryLowIcon tw="w-5 h-5" color="red" />;
  } else if (stateOfCharge && stateOfCharge > 60) {
    return <BatteryFullIcon tw="w-5 h-5" color="green" />;
  } else {
    return <BatteryMediumIcon tw="w-5 h-5" color="orange" />;
  }
};

const getSortedPieData = (areaStateAggregate: AreaStateAggregateType) => {
  return [
    {
      name: ModulesStatesEnum.UNFOLDED,
      value: areaStateAggregate.percentageOfUnfoldedModules,
    },
    {
      name: ModulesStatesEnum.UP,
      value: areaStateAggregate.percentageOfUpModules,
    },
    {
      name: ModulesStatesEnum.FOLDED,
      value: areaStateAggregate.percentageOfFoldedModules,
    },
    {
      name: ModulesStatesEnum.IN_PROGRESS,
      value: areaStateAggregate.percentageOfWaitingForStateModules,
    },
    {
      name: ModulesStatesEnum.UNDER_MAINTENANCE,
      value: areaStateAggregate.percentageOfUnderMaintenanceModules,
    },
    {
      name: ModulesStatesEnum.FAULTY,
      value: areaStateAggregate.percentageOfFaultyModules,
    },
  ].sort((a, b) => b.value - a.value);
};

const shouldDisplayModuleSticker = (
  pieData: { name: ModulesStatesEnum; value: number }[]
) => {
  return pieData.some(
    (section) =>
      [
        ModulesStatesEnum.UNFOLDED,
        ModulesStatesEnum.FOLDED,
        ModulesStatesEnum.UP,
      ].includes(section.name) && section.value > 1
  );
};

const getModuleSticker = (state: ModulesStatesEnum) => {
  switch (state) {
    case ModulesStatesEnum.UNFOLDED:
      return <Unfolded tw="w-full h-full" strokeWidth={2.5} />;
    case ModulesStatesEnum.FOLDED:
      return (
        <Folded tw="w-full h-full" viewBox="0 0 400 800" strokeWidth={2.5} />
      );
    case ModulesStatesEnum.UP:
      return <Up tw="w-full h-full" strokeWidth={2.5} />;
  }
};

function IdleStateView({
  displayedAverageBattery,
  areaStateAggregate,
  areaCommandTracking,
  commandDate,
  isToday,
  openSheet,
}: {
  displayedAverageBattery: number | null;
  areaStateAggregate: AreaStateAggregateType;
  areaCommandTracking: CommandTrackingView;
  commandDate: DateTime;
  isToday: boolean;
  openSheet: (view: SheetsType) => void;
}) {
  const openModulesList = (firstOfList: ModulesFirstOfList) =>
    openSheet({
      id: SheetsEnum.MODULES_LIST,
      firstOfList,
      canGoBack: false,
    });
  const { t: translator } = useTranslation();
  const { mutateAsync: sendCommand, isPending } = useSendCommand({
    areaId: areaStateAggregate.areaId,
  });

  return (
    <div>
      <div tw="flex flex-col gap-1 border-y border-gray-200 px-2 py-4">
        <p
          tw=" flex flex-col text-sm font-normal text-gray-400 "
          css={[tw`bigPhones:flex-row gap-1`]}
        >
          {translator("commandTracking.lastCommand")}:
          <span tw="text-sm font-medium text-gray-500">
            {" "}
            {translator(
              `modulesActions.${getLastCommandView(areaCommandTracking)}`
            )}{" "}
          </span>
        </p>
        <p tw="text-sm font-normal text-gray-400">
          {isToday
            ? `${translator("commandTracking.today")} ${commandDate.toFormat(
                "HH:mm"
              )}`
            : `${translator("commandTracking.otherDay")} ${commandDate.toFormat(
                "dd/MM/yyyy"
              )} ${translator("commandTracking.time")} ${commandDate.toFormat(
                "HH:mm"
              )}`}
        </p>
      </div>

      <div tw="flex flex-col gap-2 px-2 py-3">
        <p tw="text-sm font-normal text-gray-400 flex items-center gap-1">
          {translator("averageBattery")} :{" "}
          <span tw="font-medium text-gray-500">{displayedAverageBattery}%</span>
          {getStateOfChargeIcon(displayedAverageBattery ?? 0)}
        </p>
        <div tw="flex items-center justify-start gap-1 pb-2">
          {getBadgeButton({
            areaStateAggregate,
            openModulesList,
            translator,
          })}
          <button
            type="button"
            tw="text-sm font-normal bg-gray-200 text-gray-500 px-2 py-1 rounded-md flex items-center gap-1 h-fit"
            onClick={() => openModulesList(ModulesStatesEnum.UNDER_MAINTENANCE)}
          >
            {areaStateAggregate.numberOfUnderMaintenanceModules > 0 && (
              <Fragment>
                <WrenchIcon tw="w-4 h-4 text-gray-500" />
                <span tw="pb-0.5">
                  {areaStateAggregate.numberOfUnderMaintenanceModules}{" "}
                  {translator("modulesStates.underMaintenance")}
                </span>
              </Fragment>
            )}

            {areaStateAggregate.numberOfUnderMaintenanceModules === 0 && (
              <Fragment>
                <WrenchIcon tw="w-4 h-4 text-gray-500" />
                <span tw="pb-0.5">
                  {translator("modulesStates.noUnderMaintenance")}
                </span>
              </Fragment>
            )}
          </button>
        </div>

        {areaStateAggregate.isLocked && (
          <div tw="flex justify-between border-t border-gray-200 pt-2">
            <div tw="flex items-center gap-2 border border-gray-200 rounded-md p-2 bg-gray-100">
              <span>🚧</span>
              <p tw="text-sm font-normal text-gray-500">
                {translator("area.underMaintenance")}
              </p>
            </div>
          </div>
        )}

        <div tw="flex justify-between border-t border-gray-200 pt-4">
          <div tw="flex flex-col items-center gap-1">
            <button
              css={[
                tw`border border-gray-200 bg-gray-50 active:bg-gray-200 transition transform duration-100 flex items-center justify-center w-12 h-12 rounded-full shadow-md`,
                tw`disabled:bg-gray-100
                disabled:border-gray-200
                disabled:shadow-none
                disabled:opacity-40
                disabled:cursor-not-allowed
                disabled:active:scale-100`,
              ]}
              disabled={isPending}
              onClick={async () => {
                try {
                  await sendCommand({
                    command: ModuleCommandEnum.GET_STATUS,
                  });
                } catch (error) {
                  toast.error("Erreur lors de la récupération des statuts");
                }
              }}
            >
              <RefreshCcw tw="w-5 h-5 text-gray-600" />
            </button>
            <span tw="text-xs font-medium text-gray-600">
              {translator("modulesActions.REFRESH")}
            </span>
          </div>

          <div tw="flex flex-col items-center gap-1">
            <button
              css={[
                tw`border border-gray-200 active:bg-gray-200 transition transform duration-100 flex items-center justify-center w-12 h-12 rounded-full shadow-md`,
                tw`disabled:bg-gray-100
                disabled:border-gray-200
                disabled:shadow-none
                disabled:opacity-40
                disabled:cursor-not-allowed
                disabled:active:scale-100`,
              ]}
              disabled={areaStateAggregate.isLocked}
              onClick={
                () =>
                  openSheet({
                    id: SheetsEnum.ACTION,
                    action: ModuleCommandEnum.UNFOLD,
                    canGoBack: false,
                  }) //Je pense ue c'est a ce niveau qu'on gérera le if in progress  alors affiche bottom in progress
              }
            >
              <span tw="text-lg">☂️</span>
            </button>
            <span
              css={[
                tw`text-xs font-medium text-primary-800`,
                areaStateAggregate.isLocked && tw`text-gray-300`,
              ]}
            >
              {translator("modulesActions.UNFOLD")}
            </span>
          </div>

          <div tw="flex flex-col items-center gap-1">
            <button
              css={[
                tw`border border-gray-200 active:bg-gray-200 transition transform duration-100 flex items-center justify-center w-12 h-12 rounded-full shadow-md`,
                tw`disabled:bg-gray-100
                disabled:border-gray-200
                disabled:shadow-none
                disabled:opacity-40
                disabled:cursor-not-allowed
                disabled:active:scale-100`,
              ]}
              disabled={areaStateAggregate.isLocked}
              onClick={() =>
                openSheet({
                  id: SheetsEnum.ACTION,
                  action: ModuleCommandEnum.FOLD,
                  canGoBack: false,
                })
              }
            >
              <span tw="text-lg">🌂</span>
            </button>
            <span
              css={[
                tw`text-xs font-medium text-primary-800`,
                areaStateAggregate.isLocked && tw`text-gray-300`,
              ]}
            >
              {translator("modulesActions.FOLD")}
            </span>
          </div>

          <div tw="flex flex-col items-center gap-1">
            <button
              tw="border border-amber-500/50 bg-amber-400/30 flex items-center justify-center w-12 h-12 rounded-full shadow-md active:scale-95 active:bg-amber-100/40 transition transform duration-100"
              onClick={() =>
                openSheet({ id: SheetsEnum.AREA_LOCK, canGoBack: false })
              }
            >
              <span tw="text-lg">🚧</span>
            </button>
            <span tw="text-xs font-medium text-gray-600">
              {translator("area.outOfOrder")}
            </span>
          </div>
        </div>

        <div
          role="button"
          css={[
            tw`active:scale-95 transition transform duration-100`,
            tw`w-24 h-24 flex items-center justify-center self-center m-auto absolute top-12 translate-y-[62%] right-6 bg-white rounded-full shadow-[0_4px_10px_rgba(0,0,0,0.06),0_0_18px_rgba(0,0,0,0.06)]`,
            tw`bigPhones:top-10 bigPhones:right-2`,
          ]}
          onClick={() =>
            openModulesList(
              areaStateAggregate.numberOfFoldedModules >
                areaStateAggregate.numberOfUnfoldedModules
                ? ModulesStatesEnum.FOLDED
                : ModulesStatesEnum.UNFOLDED
            )
          }
        >
          <div
            css={[
              tw`w-24 h-24 rounded-full`,
              tw`shadow-[0_4px_10px_rgba(0,0,0,0.06),0_0_18px_rgba(0,0,0,0.06)]`,
              tw`relative`,
            ]}
          >
            <AreaStatusDonut pieData={getSortedPieData(areaStateAggregate)} />
            <div
              css={[
                tw`absolute top-4 left-[-22px] w-10 h-10 rounded-full bg-white border border-2 border-gray-300`,
                !shouldDisplayModuleSticker(
                  getSortedPieData(areaStateAggregate)
                ) && tw`hidden`,
              ]}
            >
              {shouldDisplayModuleSticker(
                getSortedPieData(areaStateAggregate)
              ) &&
                getModuleSticker(getSortedPieData(areaStateAggregate)[0].name)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type ActiveTab = "actions" | "meteo";

const TABS: { key: ActiveTab; emoji: string; labelKey: string }[] = [
  { key: "actions", emoji: "🕹️", labelKey: "area.tabs.actions" },
  { key: "meteo", emoji: "🌦️", labelKey: "area.tabs.meteo" },
];

const TabBar = ({
  activeTab,
  onChange,
}: {
  activeTab: ActiveTab;
  onChange: (tab: ActiveTab) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div tw="flex border-b border-gray-100">
      {TABS.map(({ key, emoji, labelKey }) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          css={[
            tw`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium border-b-2 transition`,
            activeTab === key
              ? tw`text-primary-800 border-primary-800`
              : tw`text-gray-500 border-transparent`,
          ]}
        >
          <span>{emoji}</span>
          {t(labelKey as any)}
        </button>
      ))}
    </div>
  );
};

const MeteoTab = ({ openSheet }: { openSheet: (view: SheetsType) => void }) => {
  const { t } = useTranslation();

  const METEO_OPTIONS = [
    {
      key: "history",
      icon: <History tw="w-5 h-5" />,
      iconBg: "#FFFFFF",
      iconColor: "#1f2937",
      label: t("meteo.climateHistory.title"),
      description: t("meteo.climateHistory.historyDesc"),
      available: true,
      onClick: () =>
        openSheet({ id: SheetsEnum.CLIMATE_HISTORY_CHOICE, canGoBack: true }),
    },
    {
      key: "protections",
      icon: <Shield tw="w-5 h-5" />,
      iconBg: "#FFFFFF",
      iconColor: "#1f2937",
      label: t("meteo.activeProtections"),
      description: t("meteo.activeProtectionsDesc"),
      available: true,
      onClick: () =>
        openSheet({ id: SheetsEnum.PROTECTION_LIST, canGoBack: true }),
    },
    {
      key: "forecast",
      icon: <CloudSun tw="w-5 h-5" />,
      iconBg: "#FFFFFF",
      iconColor: "#1f2937",
      label: t("meteo.forecast.title"),
      description: t("meteo.forecast.optionDesc"),
      available: true,
      onClick: () =>
        openSheet({ id: SheetsEnum.WEATHER_FORECAST, canGoBack: true }),
    },
  ];

  return (
    <div tw="flex flex-col px-2 py-2">
      <div tw="rounded-xl border border-gray-100 overflow-hidden">
        {METEO_OPTIONS.map((opt, i) => (
          <SelectableRow
            available={opt.available}
            key={opt.key}
            icon={opt.icon}
            iconBg={opt.iconBg}
            iconColor={opt.iconColor}
            label={opt.label}
            description={opt.description}
            active={opt.available}
            isLast={i === METEO_OPTIONS.length - 1}
            onClick={opt.onClick}
            highlightActive={false}
            activeTextColor="gray"
            trailing={
              <ChevronRight css={tw`w-4 h-4 text-green-800 shrink-0`} />
            }
          />
        ))}
      </div>
    </div>
  );
};

const AreaStateAggregate = () => {
  const { t: translator } = useTranslation();
  const sheet = useBottomSheetStack<SheetsType>();
  const [activeTab, setActiveTab] = useState<ActiveTab>("actions");

  const [lastPositiveAverageBattery, setLastPositiveAverageBattery] = useState<
    number | null
  >(null);
  const { areaStateAggregate, areaCommandTracking, areaName } =
    useAreaDataProvider();

  const commandDate = DateTime.fromJSDate(
    new Date(areaCommandTracking.timestamp)
  );
  const isToday = commandDate.hasSame(DateTime.local(), "day");

  useEffect(() => {
    const battery = areaStateAggregate.averageBattery;
    if (typeof battery === "number" && !Number.isNaN(battery) && battery > 0) {
      setLastPositiveAverageBattery(battery);
    }
  }, [areaStateAggregate.averageBattery]);

  const displayedAverageBattery =
    typeof areaStateAggregate.averageBattery === "number" &&
    !Number.isNaN(areaStateAggregate.averageBattery) &&
    areaStateAggregate.averageBattery > 0
      ? areaStateAggregate.averageBattery
      : lastPositiveAverageBattery;

  return (
    <div tw="flex flex-col rounded-xl bg-white shadow-md border border-gray-200 max-w-[775px] relative">
      <div tw="flex flex-col gap-1 p-2 rounded-t-xl bg-primary-800 text-white">
        <p tw="text-xl font-semibold">{areaName}</p>
        <p tw="text-xs font-normal">
          {areaStateAggregate.numberOfModulesInArea}{" "}
          {translator("canopies")}{" "}
        </p>
      </div>

      <TabBar activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "actions" ? (
        <IdleStateView
          displayedAverageBattery={displayedAverageBattery}
          areaStateAggregate={areaStateAggregate}
          areaCommandTracking={areaCommandTracking}
          commandDate={commandDate}
          isToday={isToday}
          openSheet={sheet.open}
        />
      ) : (
        <MeteoTab openSheet={sheet.open} />
      )}

      <BottomSheet
        open={sheet.isOpen}
        direction={sheet.direction}
        viewKey={sheet.visibleView?.id}
        onClose={sheet.close}
        onBack={
          sheet.canGoBack
            ? sheet.pop
            : sheet.visibleView?.canGoBack
              ? sheet.close
              : undefined
        }
        leadingIcon={switchSheetsLeadIcons({
          sheet,
        })}
        title={switchSheetsTitles({
          areaStateAggregate,
          areaName,
          sheet,
        })}
        headerComponent={switchSheetsHeaderComponents({
          sheet,
        })}
      >
        <SwitchSheetsRender
          sheet={sheet}
          areaStateAggregate={areaStateAggregate}
          areaCommandTracking={areaCommandTracking}
        />
      </BottomSheet>
    </div>
  );
};

export default AreaStateAggregate;
